import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

export type StreamingPhase = 'thinking' | 'tool_calling' | 'generating' | null

export interface AiMessage {
  id: string
  role: 'user' | 'assistant' | 'tool'
  content: string
  toolName?: string
  toolInput?: Record<string, unknown>
  toolResult?: string
  timestamp: string
}

export interface AiConversation {
  id: string
  title: string | null
  provider: string | null
  model: string | null
  created_at: string
  updated_at: string
}

export const useAiStore = defineStore('ai', () => {
  const isOpen = ref(false)
  const currentConversationId = ref<string | null>(null)
  const messages = ref<AiMessage[]>([])
  const isStreaming = ref(false)
  const streamingPhase = ref<StreamingPhase>(null)
  const selectedProvider = ref<string | null>(null)
  const showConversationList = ref(false)
  const isLoadingHistory = ref(false)
  const justLoadedHistory = ref(false)

  const hasMessages = computed(() => messages.value.length > 0)

  function togglePanel() {
    isOpen.value = !isOpen.value
  }

  function openPanel() {
    isOpen.value = true
  }

  function closePanel() {
    isOpen.value = false
  }

  function addMessage(message: AiMessage) {
    messages.value.push(message)
  }

  function updateLastAssistantMessage(content: string) {
    const last = messages.value.findLast(m => m.role === 'assistant')
    if (last) {
      last.content = content
    }
  }

  function startNewConversation() {
    currentConversationId.value = null
    messages.value = []
    showConversationList.value = false
  }

  function setConversation(id: string, msgs: AiMessage[]) {
    currentConversationId.value = id
    messages.value = msgs
    showConversationList.value = false
    justLoadedHistory.value = true
    setTimeout(() => {
      justLoadedHistory.value = false
    }, 1500)
  }

  function removeMessagesFromIndex(index: number) {
    messages.value.splice(index)
  }

  function setProvider(provider: string) {
    selectedProvider.value = provider
  }

  return {
    isOpen,
    currentConversationId,
    messages,
    isStreaming,
    streamingPhase,
    selectedProvider,
    showConversationList,
    isLoadingHistory,
    justLoadedHistory,
    hasMessages,
    togglePanel,
    openPanel,
    closePanel,
    addMessage,
    updateLastAssistantMessage,
    startNewConversation,
    setConversation,
    removeMessagesFromIndex,
    setProvider,
  }
})
